import React, { useState, useEffect, useContext, useMemo } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { UsuarioContext } from "../../../providers/userContext";
import { getAllEquipos, getVisitasDiariasTecnicos, logVisitaMenu } from "../../../services";
import ExcelIcon from "../../../icons/excel.svg";

const toIso = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const computeDefaultRange = () => {
  const hoy = new Date();
  const hace30 = new Date();
  hace30.setDate(hace30.getDate() - 30);
  return { desde: toIso(hace30), hasta: toIso(hoy) };
};

export default function IndicadoresTecnicos() {
  const user = useContext(UsuarioContext);
  const isAdmin = user?.rol === "ADMIN";
  const isTechCoord = user?.rol === "TECH_COOR";

  const [searchParams, setSearchParams] = useSearchParams();

  const defaultRange = useMemo(() => computeDefaultRange(), []);
  const [fechaDesde, setFechaDesde] = useState(
    searchParams.get("fecha_desde") || defaultRange.desde
  );
  const [fechaHasta, setFechaHasta] = useState(
    searchParams.get("fecha_hasta") || defaultRange.hasta
  );

  const [equipos, setEquipos] = useState([]);
  const [teamId, setTeamId] = useState(() => {
    const fromUrl = searchParams.get("team_id");
    if (fromUrl) return fromUrl;
    if (isTechCoord) return user?.equipo ?? "";
    return "";
  });

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    logVisitaMenu("indicadores_tecnicos");
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (fechaDesde) next.set("fecha_desde", fechaDesde); else next.delete("fecha_desde");
    if (fechaHasta) next.set("fecha_hasta", fechaHasta); else next.delete("fecha_hasta");
    if (isAdmin && teamId) next.set("team_id", String(teamId));
    else next.delete("team_id");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [fechaDesde, fechaHasta, teamId, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    getAllEquipos()
      .then((res) => {
        const lista = Array.isArray(res) ? res : (res?.data || []);
        setEquipos(lista);
      })
      .catch((err) => {
        console.error("Error cargando equipos:", err);
      });
  }, [isAdmin]);

  useEffect(() => {
    if (!fechaDesde || !fechaHasta) {
      setRows([]);
      return;
    }
    if (fechaDesde > fechaHasta) {
      toast.warn("La fecha desde no puede ser posterior a la fecha hasta");
      return;
    }
    const teamToUse = isTechCoord ? user?.equipo : teamId;
    if (isTechCoord && !teamToUse) {
      setRows([]);
      return;
    }

    setLoading(true);
    getVisitasDiariasTecnicos({
      team_id: teamToUse || null,
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
    })
      .then((res) => {
        const data = Array.isArray(res) ? res : (res?.data || []);
        setRows(data);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Error cargando los datos de visitas");
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [teamId, fechaDesde, fechaHasta, isTechCoord, user?.equipo]);

  const dias = useMemo(() => {
    const result = [];
    if (!fechaDesde || !fechaHasta || fechaDesde > fechaHasta) return result;
    const start = new Date(`${fechaDesde}T00:00:00`);
    const end = new Date(`${fechaHasta}T00:00:00`);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      result.push(toIso(d));
    }
    return result;
  }, [fechaDesde, fechaHasta]);

  const mismoMes = useMemo(
    () => dias.length > 0 && dias.every((d) => d.slice(0, 7) === dias[0].slice(0, 7)),
    [dias]
  );

  const { pivot, tecnicos, maxVisitas } = useMemo(() => {
    const map = {};
    let max = 0;
    for (const r of rows) {
      const key = r.tecnico ?? "—";
      if (!map[key]) map[key] = {};
      const diaIso = typeof r.dia === "string" ? r.dia.slice(0, 10) : toIso(new Date(r.dia));
      const v = Number(r.visitas) || 0;
      map[key][diaIso] = v;
      if (v > max) max = v;
    }
    return {
      pivot: map,
      tecnicos: Object.keys(map).sort((a, b) => a.localeCompare(b, "es")),
      maxVisitas: max,
    };
  }, [rows]);

  const getCellStyle = (v) => {
    if (v === 0) {
      return { background: "#ffcdd2", color: "#000" };
    }
    const intensity = maxVisitas > 0 ? v / maxVisitas : 0;
    const lightness = 88 - intensity * 55;
    return {
      background: `hsl(120, 55%, ${lightness}%)`,
      color: lightness < 55 ? "#fff" : "#1b5e20",
      fontWeight: 600,
    };
  };

  const hslToArgb = (h, s, l) => {
    const sN = s / 100;
    const lN = l / 100;
    const k = (n) => (n + h / 30) % 12;
    const a = sN * Math.min(lN, 1 - lN);
    const f = (n) => {
      const color = lN - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
      return Math.round(255 * color).toString(16).padStart(2, "0").toUpperCase();
    };
    return `FF${f(0)}${f(8)}${f(4)}`;
  };

  const cellArgb = (v) => {
    if (v === 0) return "FFFFCDD2";
    const intensity = maxVisitas > 0 ? v / maxVisitas : 0;
    const lightness = 88 - intensity * 55;
    return hslToArgb(120, 55, lightness);
  };

  const [exportando, setExportando] = useState(false);

  const exportarExcel = async () => {
    if (tecnicos.length === 0 || dias.length === 0) {
      toast.warn("No hay datos para exportar");
      return;
    }
    setExportando(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Visitas técnicos");

      const headerRow = ["Técnico", ...dias.map((d) => (mismoMes ? d.slice(8) : d.slice(5)))];
      const head = sheet.addRow(headerRow);
      head.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF92004D" } };
        cell.border = {
          top: { style: "thin", color: { argb: "FFDDDDDD" } },
          bottom: { style: "thin", color: { argb: "FFDDDDDD" } },
          left: { style: "thin", color: { argb: "FFDDDDDD" } },
          right: { style: "thin", color: { argb: "FFDDDDDD" } },
        };
      });

      tecnicos.forEach((tec) => {
        const dataRow = sheet.addRow([tec, ...dias.map((d) => pivot[tec]?.[d] || 0)]);
        dataRow.getCell(1).font = { bold: true };
        dataRow.getCell(1).alignment = { vertical: "middle", horizontal: "left" };
        for (let i = 0; i < dias.length; i++) {
          const cell = dataRow.getCell(i + 2);
          const v = pivot[tec]?.[dias[i]] || 0;
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: cellArgb(v) } };
          if (v === 0) cell.font = { color: { argb: "FF000000" } };
          else cell.font = { bold: true };
        }
      });

      sheet.getColumn(1).width = 28;
      for (let i = 2; i <= dias.length + 1; i++) {
        sheet.getColumn(i).width = mismoMes ? 6 : 10;
      }
      sheet.views = [{ state: "frozen", xSplit: 1, ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `visitas_tecnicos_${fechaDesde}_al_${fechaHasta}.xlsx`);
    } catch (err) {
      console.error("Error exportando Excel:", err);
      toast.error("Error al generar el Excel");
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="dashboard-fullscreen" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{
        padding: "16px 24px",
        display: "flex",
        gap: 16,
        alignItems: "flex-end",
        flexWrap: "wrap",
        borderBottom: "1px solid #e0e0e0",
        background: "#fafafa",
      }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>Fecha desde</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4 }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>Fecha hasta</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4 }}
          />
        </div>
        {isAdmin && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>Equipo</label>
            <select
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, minWidth: 200, background: "#fff" }}
            >
              <option value="">Todos los equipos</option>
              {equipos.map((eq) => (
                <option key={eq.id} value={eq.id}>{eq.nombre}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ marginLeft: "auto" }}>
          <button
            className={`bigBtn ${exportando || tecnicos.length === 0 ? 'disabled' : ''}`}
            onClick={exportarExcel}
            disabled={exportando || tecnicos.length === 0}
          >
            {!exportando ? (
              <>
                <img src={ExcelIcon} alt="Exportar Excel" width="24px" style={{ marginRight: '8px' }} />
                <span>Exportar Excel</span>
              </>
            ) : (
              <>
                <CircularProgress style={{ color: '#fff', width: '14px', height: '14px', marginRight: '8px' }} />
                <span>Generando...</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: 16, position: "relative", background: "#fff" }}>
        {loading && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.7)",
            zIndex: 4,
          }}>
            <CircularProgress style={{ color: "#92004D" }} />
          </div>
        )}
        {!loading && tecnicos.length === 0 ? (
          <div style={{ textAlign: "center", color: "#888", marginTop: 40 }}>
            Sin datos para los filtros seleccionados.
          </div>
        ) : (
          <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{
                  position: "sticky",
                  left: 0,
                  top: 0,
                  zIndex: 3,
                  background: "#92004D",
                  color: "#fff",
                  padding: "10px 12px",
                  borderBottom: "1px solid #ddd",
                  minWidth: 200,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                }}>Técnico</th>
                {dias.map((d) => (
                  <th key={d} style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    background: "#92004D",
                    color: "#fff",
                    padding: "10px 6px",
                    borderBottom: "1px solid #ddd",
                    minWidth: mismoMes ? 40 : 70,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}>{mismoMes ? d.slice(8) : d.slice(5)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tecnicos.map((tec, idx) => (
                <tr key={tec}>
                  <td style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    background: idx % 2 === 0 ? "#fff" : "#f7f7f7",
                    padding: "8px 12px",
                    borderBottom: "1px solid #eee",
                    borderRight: "1px solid #ddd",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}>{tec}</td>
                  {dias.map((d) => {
                    const v = pivot[tec]?.[d] || 0;
                    const cellStyle = getCellStyle(v);
                    return (
                      <td key={d} style={{
                        padding: "8px 6px",
                        borderBottom: "1px solid #eee",
                        textAlign: "center",
                        ...cellStyle,
                      }}>{v}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
